import { HttpFetcher, Locator, Manifest, Publication } from '@readium/shared'

/**
 * Publication初期化の結果
 */
export type LoadPublicationResult = {
  publication: Publication
  manifest: Manifest
  positions: Locator[]
  baseURL: string
}

/**
 * EPUBエラー表示のprops
 */
export type EpubErrorProps = {
  error: string
}

/**
 * EPUBローディング表示のprops
 */
export type EpubLoadingProps = {
  isLoading: boolean
}

/**
 * マニフェストURLからReadium Publicationを構築する共通処理
 *
 * - マニフェスト取得・パース
 * - Publication構築 (認証付きfetcher)
 * - ポジションリスト取得 (バックエンド → fallback: manifest)
 */
export async function loadEpubPublication(
  authFetch: typeof fetch,
  manifestUrl: string,
): Promise<LoadPublicationResult> {
  const response = await authFetch(manifestUrl)
  if (!response.ok) throw new Error(`マニフェスト取得失敗: ${response.status}`)

  const json = await response.json()
  const manifest = Manifest.deserialize(json)
  if (!manifest) throw new Error('マニフェストのパース失敗')
  manifest.setSelfLink(manifestUrl)

  const baseURL = manifest.baseURL ?? manifestUrl.replace(/\/[^/]*$/, '/')
  const fetcher = new HttpFetcher(authFetch, baseURL)
  const publication = new Publication({ manifest, fetcher })

  // ポジションリスト取得
  let positions: Locator[] = []
  const posLinks = manifest.linksWithRel('positions')
  if (posLinks.length > 0) {
    const posUrl = posLinks[0].toURL(baseURL)
    if (posUrl) {
      const posResp = await authFetch(posUrl)
      if (posResp.ok) {
        const posJson = await posResp.json()
        const posArray = posJson.positions ?? posJson
        positions = (posArray as unknown[])
          .map((p) => Locator.deserialize(p))
          .filter((l): l is Locator => l !== undefined)
      }
    }
  }

  if (positions.length === 0) {
    positions = await publication.positionsFromManifest()
  }

  return { publication, manifest, positions, baseURL }
}
